/**
 * Shape Metadata Service
 * 
 * Manages shape-level metadata using PowerPoint's CustomXmlParts API.
 * This is more robust than shape names or alt text because:
 * - Metadata persists when shapes are copied/moved
 * - Structured data can be stored (not just strings)
 * - Doesn't interfere with user-visible properties
 * 
 * Requires PowerPointApi 1.7+
 */

import type { MetadataEntry } from './metadataTypes';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const CAROUSEL_NAMESPACE = "http://schemas.slideknowledgegraph.com/carouselshape";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Shape metadata stored in CustomXmlPart
 */
export interface CarouselShapeMetadata {
    /** Unified metadata entries for this shape */
    entries?: MetadataEntry[];
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Escape special XML characters
 */
function escapeXml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

/**
 * Convert metadata to XML string.
 * Includes shapeId for matching when reading back from PPTX file.
 */
function metadataToXml(metadata: CarouselShapeMetadata, shapeId: string): string {
    const entriesJson = metadata.entries ? escapeXml(JSON.stringify(metadata.entries)) : "";

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<CarouselShape xmlns="${CAROUSEL_NAMESPACE}">
  <ShapeId>${escapeXml(shapeId)}</ShapeId>
  ${entriesJson ? `<Entries>${entriesJson}</Entries>` : ''}
</CarouselShape>`;
}

/**
 * Parse metadata from XML string
 */
function xmlToMetadata(xml: string): CarouselShapeMetadata | null {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, "text/xml");
        
        const getElement = (tag: string): string | null => {
            const el = doc.getElementsByTagName(tag)[0];
            return el?.textContent || null;
        };
        
        const entriesStr = getElement("Entries");

        const entries = entriesStr ? JSON.parse(entriesStr) : undefined;
        const hasEntries = Boolean(entries && (entries as MetadataEntry[]).length);

        if (!hasEntries) {
            return null;
        }

        return {
            entries,
        };
    } catch (e) {
        console.warn("[ShapeMetadata] Failed to parse XML:", e);
        return null;
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// API CHECK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if PowerPointApi 1.7 is available (required for shape.customXmlParts)
 */
export function isShapeMetadataSupported(): boolean {
    // @ts-ignore - Office.context.requirements may not be in type definitions
    return Office.context.requirements?.isSetSupported?.('PowerPointApi', '1.7') ?? false;
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN API
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Set metadata on a shape using CustomXmlParts.
 * 
 * @param slideIndex 0-based slide index
 * @param shapeId PowerPoint shape ID
 * @param metadata The metadata to store
 */
export async function setShapeMetadata(
    slideIndex: number,
    shapeId: string,
    metadata: CarouselShapeMetadata
): Promise<boolean> {
    if (!isShapeMetadataSupported()) {
        console.warn("[ShapeMetadata] PowerPointApi 1.7 not supported, cannot set shape metadata");
        return false;
    }
    
    return PowerPoint.run(async (context) => {
        try {
            const slides = context.presentation.slides;
            slides.load("items");
            await context.sync();
            
            if (slideIndex >= slides.items.length) {
                console.error(`[ShapeMetadata] Slide index ${slideIndex} out of range`);
                return false;
            }
            
            const slide = slides.items[slideIndex];
            const shapes = slide.shapes;
            shapes.load("items");
            await context.sync();
            
            // Find shape by ID
            const shape = shapes.items.find(s => String(s.id) === shapeId);
            if (!shape) {
                console.error(`[ShapeMetadata] Shape ${shapeId} not found on slide ${slideIndex}`);
                return false;
            }
            
            // Remove any existing Carousel metadata first
            const existingParts = shape.customXmlParts.getByNamespace(CAROUSEL_NAMESPACE);
            existingParts.load("items");
            await context.sync();
            
            for (const part of existingParts.items) {
                part.delete();
            }
            await context.sync();
            
            // Add new metadata (include shapeId for PPTX file matching)
            const xml = metadataToXml(metadata, shapeId);
            shape.customXmlParts.add(xml);
            await context.sync();
            
            console.log(`[ShapeMetadata] Set metadata on shape ${shapeId}:`, {
                entries: metadata.entries?.length ?? 0,
            });
            
            return true;
        } catch (e) {
            console.error("[ShapeMetadata] Failed to set shape metadata:", e);
            return false;
        }
    });
}

/**
 * Get metadata from a shape.
 * 
 * @param slideIndex 0-based slide index
 * @param shapeId PowerPoint shape ID
 * @returns CarouselShapeMetadata or null if not found
 */
export async function getShapeMetadata(
    slideIndex: number,
    shapeId: string
): Promise<CarouselShapeMetadata | null> {
    if (!isShapeMetadataSupported()) {
        return null;
    }
    
    return PowerPoint.run(async (context) => {
        try {
            const slides = context.presentation.slides;
            slides.load("items");
            await context.sync();
            
            if (slideIndex >= slides.items.length) {
                return null;
            }
            
            const slide = slides.items[slideIndex];
            const shapes = slide.shapes;
            shapes.load("items");
            await context.sync();
            
            const shape = shapes.items.find(s => String(s.id) === shapeId);
            if (!shape) {
                return null;
            }
            
            const carouselParts = shape.customXmlParts.getByNamespace(CAROUSEL_NAMESPACE);
            carouselParts.load("items");
            await context.sync();
            
            if (carouselParts.items.length === 0) {
                return null;
            }
            
            const xmlResult = carouselParts.items[0].getXml();
            await context.sync();
            
            return xmlToMetadata(xmlResult.value);
        } catch (e) {
            console.error("[ShapeMetadata] Failed to get shape metadata:", e);
            return null;
        }
    });
}

/**
 * Find all shapes with Carousel metadata on a slide.
 * 
 * @param slideIndex 0-based slide index
 * @returns Array of { shapeId, metadata }
 */
export async function findCarouselShapesOnSlide(
    slideIndex: number
): Promise<Array<{ shapeId: string; metadata: CarouselShapeMetadata }>> {
    if (!isShapeMetadataSupported()) {
        return [];
    }
    
    return PowerPoint.run(async (context) => {
        const results: Array<{ shapeId: string; metadata: CarouselShapeMetadata }> = [];
        
        try {
            const slides = context.presentation.slides;
            slides.load("items");
            await context.sync();
            
            if (slideIndex >= slides.items.length) {
                return results;
            }
            
            const slide = slides.items[slideIndex];
            const shapes = slide.shapes;
            shapes.load("items");
            await context.sync();
            
            for (const shape of shapes.items) {
                try {
                    const carouselParts = shape.customXmlParts.getByNamespace(CAROUSEL_NAMESPACE);
                    carouselParts.load("items");
                    await context.sync();
                    
                    if (carouselParts.items.length > 0) {
                        const xmlResult = carouselParts.items[0].getXml();
                        await context.sync();
                        
                        const metadata = xmlToMetadata(xmlResult.value);
                        if (metadata) {
                            results.push({
                                shapeId: String(shape.id),
                                metadata,
                            });
                        }
                    }
                } catch (e) {
                    // Shape might not support customXmlParts, skip
                }
            }
        } catch (e) {
            console.error("[ShapeMetadata] Failed to find Carousel shapes:", e);
        }
        
        return results;
    });
}

/**
 * Find all Carousel shapes in the entire presentation.
 * 
 * @returns Array of { slideIndex, shapeId, metadata }
 */
export async function findAllCarouselShapes(): Promise<
    Array<{ slideIndex: number; shapeId: string; metadata: CarouselShapeMetadata }>
> {
    if (!isShapeMetadataSupported()) {
        return [];
    }
    
    const results: Array<{ slideIndex: number; shapeId: string; metadata: CarouselShapeMetadata }> = [];
    
    try {
        // Get slide count first
        const slideCount = await PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            slides.load("items");
            await context.sync();
            return slides.items.length;
        });
        
        // Query each slide (separate PowerPoint.run calls to avoid context issues)
        for (let i = 0; i < slideCount; i++) {
            const slideResults = await findCarouselShapesOnSlide(i);
            for (const result of slideResults) {
                results.push({
                    slideIndex: i,
                    ...result,
                });
            }
        }
    } catch (e) {
        console.error("[ShapeMetadata] Failed to find all Carousel shapes:", e);
    }
    
    return results;
}

/**
 * Remove Carousel metadata from a shape.
 * 
 * @param slideIndex 0-based slide index
 * @param shapeId PowerPoint shape ID
 */
export async function removeShapeMetadata(
    slideIndex: number,
    shapeId: string
): Promise<boolean> {
    if (!isShapeMetadataSupported()) {
        return false;
    }
    
    return PowerPoint.run(async (context) => {
        try {
            const slides = context.presentation.slides;
            slides.load("items");
            await context.sync();
            
            if (slideIndex >= slides.items.length) {
                return false;
            }
            
            const slide = slides.items[slideIndex];
            const shapes = slide.shapes;
            shapes.load("items");
            await context.sync();
            
            const shape = shapes.items.find(s => String(s.id) === shapeId);
            if (!shape) {
                return false;
            }
            
            const carouselParts = shape.customXmlParts.getByNamespace(CAROUSEL_NAMESPACE);
            carouselParts.load("items");
            await context.sync();
            
            for (const part of carouselParts.items) {
                part.delete();
            }
            await context.sync();
            
            console.log(`[ShapeMetadata] Removed metadata from shape ${shapeId}`);
            return true;
        } catch (e) {
            console.error("[ShapeMetadata] Failed to remove shape metadata:", e);
            return false;
        }
    });
}

/**
 * Find a shape by artifact ID across all slides.
 * 
 * @param artifactId The artifact ID to search for
 * @returns Shape location and metadata, or null if not found
 */
export async function findShapeByArtifactId(
    artifactId: string
): Promise<{ slideIndex: number; shapeId: string; metadata: CarouselShapeMetadata } | null> {
    const allShapes = await findAllCarouselShapes();
    return allShapes.find(s =>
        s.metadata.entries?.some(entry => (entry.payload as { artifactId?: string } | undefined)?.artifactId === artifactId)
    ) || null;
}

